import React, { useEffect, useState, useContext } from 'react'
import ModalTest from '../../../BaseModal'
import { Button, ButtonContainer, CloseDiv, ContainerOfModal, Header, HeaderTitle, ViewContainer, ViewItem } from '../../../styles/modal-style'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ApiService from '../../../../services/ApiService'
import { X, Trash2 } from 'lucide-react'
import { Label } from '../../../styles/select-style'
import editPen from '../../../../assets/certificates/Tables/pencil.svg'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../ModalConfirmation'

interface Supplier {
  id: number
  companyName: string
}

interface Resource {
  id: number
  name: string
  code: string
  category: { id: number, name: string } | null
  type: { id: number, name: string } | null
  pricePerKg: number
  prices?: Array<{
    id: number
    price: string
    unitOfMeasurement: {
      id: number
      name: string
    }
  }>
  description: string
  suppliers: Supplier[]
}

const ViewResourceModal = () => {
  const [ searchParams ] = useSearchParams()
  const id = searchParams.get('id')
  const navigate = useNavigate()
  const [ resource, setResource ] = useState<Resource | null>(null)
  const { setFeedback } = useContext(FeedbackContext)
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, resourceId: null as number | null, resourceName: '' })

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        try {
          const response = await ApiService.resources.findById(Number(id))
          const data = response.data
          setResource(data)
        } catch (error) {
          console.log(error)
        }
      }
    }
    fetchData()
  }, [id])

  const formatPrice = (price?: number | null) => {
    if (!price) return 'N/A'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const formatPrices = (prices?: any[]) => {
    if (!prices || prices.length === 0) return 'N/A'
    
    return prices.map(price => {
      const formattedPrice = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(parseFloat(price.price))
      return `${formattedPrice}/${price.unitOfMeasurement?.name || 'unidade'}`
    }).join(', ')
  }

  const handleDeleteClick = () => {
    if (!id || !resource) return
    setDeleteModal({ isOpen: true, resourceId: parseInt(id), resourceName: resource.name })
  }

  const handleConfirmDelete = async () => {
    if (!deleteModal.resourceId) return
    try {
      await ApiService.resources.delete(deleteModal.resourceId)
      setFeedback({ 
        message: 'Insumo excluído com sucesso!', 
        isOpen: true, 
        statusOk: true 
      })
  navigate('/panel/stock/resource')
    } catch (error: any) {
      let errorMessage = 'Erro ao excluir insumo'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      setFeedback({ 
        message: errorMessage, 
        isOpen: true, 
        statusOk: false 
      })
    } finally {
      setDeleteModal({ isOpen: false, resourceId: null, resourceName: '' })
    }
  }

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, resourceId: null, resourceName: '' })
  }

  return (
    <>
      <ModalTest>
        <ContainerOfModal width='800px'>
          <Header>
            <HeaderTitle>Insumo: {resource?.name}</HeaderTitle>
            <CloseDiv onClick={() => navigate('/panel/stock/resource')}> <X size={20} /></CloseDiv>
          </Header>        
          <ViewContainer>
            <ViewItem>
              <span>Categoria</span>
              <Label>{resource?.category?.name || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Tipo</span>
              <Label>{resource?.type?.name || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Código</span>
              <Label>{resource?.code || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Preços</span>
              <Label>{formatPrices(resource?.prices)}</Label>
            </ViewItem>
            <ViewItem>
              <span>Fornecedores</span>
              <Label>{resource?.suppliers.map((supplier: any) => supplier.companyName).join(', ') || 'N/A'}</Label>
            </ViewItem>
            <ViewItem>
              <span>Descrição</span>
              <Label>{resource?.description || 'N/A'}</Label>
            </ViewItem>
          </ViewContainer>
          <ButtonContainer>
            <Button 
              onClick={handleDeleteClick}
              style={{
                backgroundColor: '#F87171',
                color: '#FFFFFF',
                border: '1px solid #F87171'
              }}
            >
              <Trash2 size={16} /> Excluir
            </Button>
            <Button 
              onClick={() => navigate(`/panel/stock/resource/edit?id=${id}`)}
              className='cancel'
            >
              <img src={editPen} alt='edit' /> Editar
            </Button>
          </ButtonContainer>
        </ContainerOfModal>
      </ModalTest>
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        title="Confirmar Exclusão"
        message={
          <div>
            <p>Tem certeza que deseja excluir o insumo: &quot;{deleteModal.resourceName}&quot;?</p>
            <p>Esta ação não pode ser desfeita.</p>
          </div>
        }
        actions={[
          { label: 'Cancelar', onClick: handleCancelDelete, variant: 'outline' },
          { label: 'Excluir', onClick: handleConfirmDelete, variant: 'danger' },
        ]}
      />
    </>
  )
}

export default ViewResourceModal
